import React from "react"
import { t } from "i18next"
import { X, BookOpen, Sliders } from "lucide-react"
import { ModalProps } from "./index"
import { useSettings } from "@/components/Settings/useSettings"
import { UserTypes } from "@/api"
import { C } from "@/theme/newDesignTokens"
import * as s from "./styles"

interface WelcomeModalProps extends ModalProps {
    onDoNotShowAgainChange?: (doNotShowAgain: boolean) => void
}

const WelcomeModal: React.FC<WelcomeModalProps> = ({ open, close }) => {
    const { onUserTypeChange, showWelcomeMessage, onShowWelcomeMessageChange } = useSettings()
    const [hovered, setHovered] = React.useState<string | null>(null)

    const handleUserTypeSelection = (userType: UserTypes) => {
        onUserTypeChange({ target: { value: JSON.stringify({ key: userType }) } } as any)
        close()
    }

    const handleDoNotShowAgainChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        onShowWelcomeMessageChange(!event.target.checked)
    }

    if (!open) return null

    return (
        <div style={s.overlay} onClick={close}>
            <div style={s.welcomeCard} onClick={(e) => e.stopPropagation()}>
                {/* Close button */}
                <button onClick={close} style={s.welcomeCloseButton}>
                    <X size={18} color={C.medGray} />
                </button>

                {/* Title */}
                <div style={s.welcomeTitleRow}>
                    <div style={s.welcomeTitle}>{t("Welcome to Aidocr!")}</div>
                </div>

                {/* Subtitle */}
                <div style={s.welcomeSubtitle}>
                    {t("Personalize your experience by choosing the mode that suits your workflow. You can change this later in the user menu.")}
                </div>

                {/* Mode cards */}
                <div style={s.modeCardsRow}>
                    {/* Guided Mode */}
                    <button
                        onMouseEnter={() => setHovered("beginner")}
                        onMouseLeave={() => setHovered(null)}
                        onClick={() => handleUserTypeSelection(UserTypes.Beginner)}
                        style={s.getModeCard(hovered === "beginner")}
                    >
                        <BookOpen size={28} color="#2e7d32" />
                        <div style={s.modeCardTitle}>{t("Guided Mode")}</div>
                        <div style={s.modeCardDescription}>
                            {t("For users seeking a simple and fast experience.")}
                        </div>
                    </button>

                    {/* Advanced Mode */}
                    <button
                        onMouseEnter={() => setHovered("expert")}
                        onMouseLeave={() => setHovered(null)}
                        onClick={() => handleUserTypeSelection(UserTypes.Expert)}
                        style={s.getModeCard(hovered === "expert")}
                    >
                        <Sliders size={28} color={C.navy} />
                        <div style={s.modeCardTitle}>{t("Advanced Mode")}</div>
                        <div style={s.modeCardDescription}>
                            {t("For users who want full control and customization.")}
                        </div>
                    </button>
                </div>

                {/* Checkbox */}
                <div style={s.checkboxRow}>
                    <label style={s.checkboxLabel}>
                        <input
                            type="checkbox"
                            checked={!showWelcomeMessage}
                            onChange={handleDoNotShowAgainChange}
                            style={s.checkboxInput}
                        />
                        {t("Do not show this message again")}
                    </label>
                </div>
            </div>
        </div>
    )
}

export default WelcomeModal
